// Durable child-agent coordination contracts (PRD stage 5, AC23-AC28; migrations 0036 and 0043).
import { z } from "zod";

const id = z.uuid();
const permission = z.string().trim().min(1).max(160).regex(/^[A-Za-z0-9][A-Za-z0-9:._/-]*$/);
const text = (max: number) => z.string().trim().min(1).max(max);

export const COORDINATION_MESSAGE_KINDS = [
  "request",
  "report",
  "cancel",
  "sync",
  "question",
  "help_request",
  "contract_change",
  "dependency_ready",
  "evidence",
  "review",
  "delivery",
  "cancellation",
] as const;
export type CoordinationMessageKind = (typeof COORDINATION_MESSAGE_KINDS)[number];

/** Kinds the recipient must answer; an unanswered one past its deadline is overdue. */
export const REPLY_REQUIRED_KINDS = ["question", "help_request", "contract_change", "review"] as const satisfies readonly CoordinationMessageKind[];

/** A delegated child never receives this authority: a grant binds one human-approved operation to its task (stage 4). */
export const CHILD_FORBIDDEN_PERMISSIONS: readonly string[] = ["tool:request_operation"];

export const RESOURCE_RESERVATION_KINDS = ["file", "effect"] as const;
export type ResourceReservationKind = (typeof RESOURCE_RESERVATION_KINDS)[number];

export const CHILD_RUN_STATES = ["running", "waiting", "succeeded", "failed", "cancelled"] as const;
export type ChildRunState = (typeof CHILD_RUN_STATES)[number];

export const SUBTASK_ACTIONS = ["guide", "pause", "resume", "cancel", "replace"] as const;
export type SubtaskAction = (typeof SUBTASK_ACTIONS)[number];

export const permissionSetSchema = z.array(permission).max(256);
export type PermissionSet = z.infer<typeof permissionSetSchema>;

export function normalizePermissions(values: readonly string[]): string[] {
  return [...new Set(permissionSetSchema.parse(values))].sort();
}

/** Effective authority can only shrink: parent authority ∩ requested child authority. */
export function intersectPermissions(parent: readonly string[], requested: readonly string[]): string[] {
  const allowed = new Set(normalizePermissions(parent));
  return normalizePermissions(requested).filter((value) => allowed.has(value));
}

export const createChildRunCoordinationSchema = z
  .object({
    project_id: id,
    parent_run_id: id,
    child_run_id: id,
    pinned_agent_profile_version_id: id,
    pinned_model_target_id: id,
    pinned_routing_decision_id: id.nullable().default(null),
    requested_permissions: permissionSetSchema.default([]),
    parent_permissions: permissionSetSchema.default([]),
    spawn_key: z.string().trim().min(1).max(200),
    max_depth: z.number().int().min(1).max(32).default(8),
    task_id: id.nullable().default(null),
    workflow_id: text(200).nullable().default(null),
    budget_calls: z.number().int().min(1).max(200).nullable().default(null),
    workspace_ref: text(300).nullable().default(null),
    args_hash: z.string().regex(/^[0-9a-f]{64}$/).nullable().default(null),
  })
  .strict();
/** Raw input: fields with defaults may be omitted by callers (the repository parses them). */
export type CreateChildRunCoordination = z.input<typeof createChildRunCoordinationSchema>;

export const coordinationMessageSchema = z
  .object({
    project_id: id,
    sender_run_id: id,
    recipient_run_id: id,
    kind: z.enum(COORDINATION_MESSAGE_KINDS),
    dedup_key: z.string().trim().min(1).max(240),
    payload: z.record(z.string(), z.unknown()).default({}),
    reply_to_id: id.nullable().default(null),
    deadline_at: z.iso.datetime().nullable().default(null),
    // 255 is PostgreSQL's regex repetition limit: migration 0043 must use the same bound (its 280 is rejected by the server).
    content_ref: z.string().regex(/^(artifact|evidence):[A-Za-z0-9:._-]{1,255}$/).nullable().default(null),
  })
  .strict();
export type CoordinationMessageInput = z.input<typeof coordinationMessageSchema>;

/** Arguments of the parent's `spawn_subtask` tool call; the child's identity comes from the spawn key, never from here. */
export const spawnSubtaskArgsSchema = z
  .object({
    agent: text(120),
    objective: text(4000),
    acceptance_criteria: z.array(text(1000)).max(50).default([]),
    constraints: z.array(text(1000)).max(50).default([]),
    references: z.array(text(300)).max(50).default([]),
    owned_paths: z.array(text(1024)).max(100).default([]),
    permissions: permissionSetSchema.default([]),
    budget_calls: z.number().int().min(1).max(200),
  })
  .strict();
export type SpawnSubtaskArgs = z.infer<typeof spawnSubtaskArgsSchema>;

export const subtaskActionSchema = z
  .object({
    action: z.enum(SUBTASK_ACTIONS),
    reason: text(1000),
    message: text(4000).optional(),
    agent_key: text(120).optional(),
    confirm: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) => (v.action !== "guide" || v.message !== undefined) && (v.action !== "replace" || v.agent_key !== undefined),
    { message: "guide needs a message; replace needs an agent_key" },
  );
export type SubtaskActionInput = z.infer<typeof subtaskActionSchema>;

/** Read model of one durable subtask for the supervisor API. */
export const subtaskViewSchema = z.object({
  id: id,
  child_run_id: id,
  parent_run_id: id,
  task_id: id.nullable(),
  attempt_id: id.nullable(),
  workflow_id: z.string().nullable(),
  state: z.enum(CHILD_RUN_STATES),
  budget_calls: z.number().int().nullable(),
  effective_permissions: permissionSetSchema,
  workspace_ref: z.string().nullable(),
  result_ref: z.string().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type SubtaskView = z.infer<typeof subtaskViewSchema>;

export const resourceReservationSchema = z
  .object({
    project_id: id,
    child_run_id: id,
    resource_kind: z.enum(RESOURCE_RESERVATION_KINDS),
    resource_key: z.string().trim().min(1).max(1024),
    ttl_ms: z.number().int().min(100).max(86_400_000),
  })
  .strict();
export type ResourceReservationInput = z.infer<typeof resourceReservationSchema>;
